import { type FC, type PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';

export interface TenantUser {
  id: string;
  name: string | null;
  email: string | null;
  externalId: string | null;
  displayName: string | null;
}

// GraphQL query for tenant users
const TENANT_USERS_QUERY = gql`
  query TenantUsers($activeOnly: Boolean) @endpoint(name: studio) {
    tenantUsers(activeOnly: $activeOnly) {
      id
      name
      email
      externalId
      displayName
    }
  }
`;

interface TenantUsersQueryData {
  tenantUsers: TenantUser[];
}

interface TenantUsersQueryVariables {
  activeOnly?: boolean;
}

export interface TenantUsersContextType {
  users: TenantUser[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  getUserById: (id: string) => TenantUser | undefined;
  getUserByEmail: (email: string) => TenantUser | undefined;
}

const TenantUsersContext = createContext<TenantUsersContextType | undefined>(undefined);

export const TenantUsersProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, loading, error, refetch } = useQuery<
    TenantUsersQueryData,
    TenantUsersQueryVariables
  >(TENANT_USERS_QUERY, {
    variables: { activeOnly: true },
    fetchPolicy: 'cache-first',
  });

  const users = useMemo(() => data?.tenantUsers || [], [data?.tenantUsers]);

  const getUserById = useMemo(
    () =>
      (id: string): TenantUser | undefined => {
        return users.find((user: TenantUser) => user.id === id);
      },
    [users]
  );

  const getUserByEmail = useMemo(
    () =>
      (email: string): TenantUser | undefined => {
        return users.find((user: TenantUser) => user.email === email);
      },
    [users]
  );

  const contextValue: TenantUsersContextType = useMemo(
    () => ({
      users,
      isLoading: loading,
      error: error || null,
      refetch,
      getUserById,
      getUserByEmail,
    }),
    [users, loading, error, refetch, getUserById, getUserByEmail]
  );

  return <TenantUsersContext.Provider value={contextValue}>{children}</TenantUsersContext.Provider>;
};

/**
 * Hook to access tenant users data.
 * Must be used within a TenantUsersProvider.
 */
export const useTenantUsers = (): TenantUsersContextType => {
  const context = useContext(TenantUsersContext);

  if (!context) {
    throw new Error('useTenantUsers must be used within a TenantUsersProvider');
  }

  return context;
};
